// @flow

import React, { PureComponent } from 'react';
import { createPaginationContainer, graphql } from 'react-relay';
import debounce from 'lodash.debounce';
import { BrowserUtils, i18n } from 'shared/utils';
import * as Actions from 'main-app/store/Actions';
import LoadingMore from 'shared/components/common/LoadingMore';
import EmptySearchResults from 'shared/components/common/EmptySearchResults';
import Table from 'shared/components/common/Table';
import Icon from 'shared/components/common/Icon';
import AddUpdateWarehouseLocationModal from 'main-app/components/AddUpdateWarehouseLocationModal';
import DeleteWarehouseLocationMutation from 'main-app/mutations/DeleteWarehouseLocation';
import type { PaginatedWarehouseLocationsContainer_data as DataFragment } from './__generated__/PaginatedWarehouseLocationsContainer_data';

type Props = {
  data: DataFragment,
  filtersDirty: boolean,
  onCreateNew: () => void,
  relay: {
    environment: Object,
    hasMore: () => boolean,
    isLoading: () => boolean,
    loadMore: (pageSize: number, callback: Function) => void,
  },
};

type State = {
  editLocation: ?Object,
};

class PaginatedWarehouseLocationsContainer extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { editLocation: null };
    this.onScrollEndReached = debounce(this.onScrollEndReached, 100);
  }

  componentDidMount = () => {
    document.addEventListener('scroll', this.onScrollEndReached);
  };

  componentWillUnmount = () => {
    document.removeEventListener('scroll', this.onScrollEndReached);
  };

  onScrollEndReached = () => {
    const { relay } = this.props;
    if (BrowserUtils.scrollEndReached(200)) {
      if (relay.hasMore()) {
        relay.loadMore(30);
      }
    }
  };

  handleDelete = async (location: Object) => {
    try {
      await DeleteWarehouseLocationMutation.commit({
        variables: { input: { id: location.id } },
      });
      Actions.alertNotification(
        i18n.t('Warehouse Location Deleted'),
        'success',
      );
    } catch (e) {
      Actions.alertNotification(e.message, 'error');
    }
  };

  render() {
    const { data, relay, filtersDirty } = this.props;
    const { editLocation } = this.state;
    const edges = data.warehouseLocations?.edges || [];

    if (!edges.length && filtersDirty) {
      return <EmptySearchResults />;
    }

    const columns = [
      {
        Header: i18n.t('Name'),
        accessor: 'node.name',
        minWidth: 200,
      },
      {
        Header: i18n.t('Code'),
        accessor: 'node.code',
        width: 150,
        Cell: ({ value }) => value || '—',
      },
      {
        Header: i18n.t('Description'),
        accessor: 'node.description',
        minWidth: 250,
        Cell: ({ value }) => value || '—',
      },
      {
        Header: i18n.t('Status'),
        accessor: 'node.isActive',
        width: 100,
        Cell: ({ value }) => (value ? i18n.t('Active') : i18n.t('Inactive')),
      },
      {
        Header: '',
        accessor: 'node.id',
        width: 110,
        sortable: false,
        Cell: ({ original }) => (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <Icon
              type="circle-edit"
              size={16}
              onClick={e => {
                e.stopPropagation();
                this.setState({ editLocation: original.node });
              }}
              style={{ cursor: 'pointer' }}
            />
            <Icon
              type="circle-trash"
              size={16}
              onClick={e => {
                e.stopPropagation();
                this.handleDelete(original.node);
              }}
              style={{ cursor: 'pointer' }}
            />
            <Icon
              type="circle-new-window"
              size={16}
              onClick={e => {
                e.stopPropagation();
                window.open(
                  `/api/v1/barcode/location/${original.node.id}`,
                  '_blank',
                );
              }}
              style={{ cursor: 'pointer' }}
              title={i18n.t('Print Barcode')}
            />
          </div>
        ),
      },
    ];

    return (
      <>
        <Table columns={columns} data={edges} pageSize={edges.length || 1} noDataText="" />
        {relay.hasMore() && relay.isLoading() ? <LoadingMore /> : null}
        {editLocation && (
          <AddUpdateWarehouseLocationModal
            onClose={() => this.setState({ editLocation: null })}
            location={editLocation}
          />
        )}
      </>
    );
  }
}

export default createPaginationContainer(
  PaginatedWarehouseLocationsContainer,
  {
    data: graphql`
      fragment PaginatedWarehouseLocationsContainer_data on Query
        @argumentDefinitions(
          count: { type: "Int", defaultValue: 30 }
          cursor: { type: "String" }
          search: { type: "String" }
        ) {
        warehouseLocations(
          first: $count
          after: $cursor
          search: $search
        )
          @connection(
            key: "PaginatedWarehouseLocationsContainer_warehouseLocations"
            filters: []
          ) {
          edges {
            cursor
            node {
              id
              name
              code
              description
              isActive
              updatedAt
              createdAt
            }
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps: props => props.data?.warehouseLocations,
    getVariables: (props, { count, cursor }) => ({
      count,
      cursor,
    }),
    query: graphql`
      query PaginatedWarehouseLocationsContainerPaginationQuery(
        $count: Int!
        $cursor: String
        $search: String
      ) {
        ...PaginatedWarehouseLocationsContainer_data
          @arguments(count: $count, cursor: $cursor, search: $search)
      }
    `,
  },
);
